import { useCallback, useEffect, useRef, useState } from 'react'
import type { ConnectionStatus } from '../../hooks/use-sse-stream.ts'
import type { StatusNotificationStatus } from './status-notification.tsx'

export type UseActivityTabBehaviorOptions<Tab extends string> = {
  isLive: boolean
  connectionStatus: ConnectionStatus | undefined
  defaultTab: Tab
  activityTab: Tab
  isActivityTabVisible: boolean
  shouldShowConfetti: boolean
}

type NotificationState = {
  status: StatusNotificationStatus
  text: string
}

type ActivityTabBehavior<Tab extends string> = {
  selectedTab: Tab
  handleTabChange: (value: string) => unknown
  notification: NotificationState | undefined
  switchToActivityTab: () => unknown
  showConfirmation: boolean
  showConfetti: boolean
  onConfettiComplete: () => unknown
}

const AUTO_TRANSITION_DELAY_MS = 2000

export const useActivityTabBehavior = <Tab extends string>({
  isLive,
  connectionStatus,
  defaultTab,
  activityTab,
  isActivityTabVisible,
  shouldShowConfetti,
}: UseActivityTabBehaviorOptions<Tab>): ActivityTabBehavior<Tab> => {
  const [selectedTab, setSelectedTab] = useState<Tab>(isLive && isActivityTabVisible ? activityTab : defaultTab)
  const [notification, setNotification] = useState<NotificationState>()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)

  const prevConnectionStatusRef = useRef(connectionStatus)
  const hasShownAutoTransitionConfettiRef = useRef(false)
  const isConfirmationPendingRef = useRef(false)
  const wasEverLiveRef = useRef(isLive)
  if (isLive) {
    wasEverLiveRef.current = true
  }

  // Stream completion: connectionStatus transitions to 'finished'
  useEffect(() => {
    if (wasEverLiveRef.current && prevConnectionStatusRef.current !== 'finished' && connectionStatus === 'finished') {
      if (selectedTab !== activityTab) {
        setNotification({ status: 'available', text: 'Analysis complete' })
      } else {
        isConfirmationPendingRef.current = true
        setShowConfirmation(true)
      }
    }

    if (connectionStatus === 'connecting' && prevConnectionStatusRef.current !== 'connecting' && isLive) {
      hasShownAutoTransitionConfettiRef.current = false
      isConfirmationPendingRef.current = false
    }

    prevConnectionStatusRef.current = connectionStatus
  }, [connectionStatus, selectedTab, activityTab])

  // Auto-transition: 2s confirmation screen then switch to default tab
  useEffect(() => {
    if (!showConfirmation) return
    const timer = setTimeout(() => {
      isConfirmationPendingRef.current = false
      setShowConfirmation(false)
      setSelectedTab(defaultTab)
      if (shouldShowConfetti && !hasShownAutoTransitionConfettiRef.current) {
        hasShownAutoTransitionConfettiRef.current = true
        setShowConfetti(true)
      }
    }, AUTO_TRANSITION_DELAY_MS)
    return () => clearTimeout(timer)
  }, [showConfirmation, defaultTab, shouldShowConfetti])

  // Historical confetti: fires for non-live success results
  useEffect(() => {
    if (
      wasEverLiveRef.current &&
      shouldShowConfetti &&
      connectionStatus !== 'connecting' &&
      connectionStatus !== 'connected' &&
      !showConfirmation &&
      !isConfirmationPendingRef.current &&
      !hasShownAutoTransitionConfettiRef.current
    ) {
      hasShownAutoTransitionConfettiRef.current = true
      setShowConfetti(true)
    }
  }, [shouldShowConfetti, connectionStatus, showConfirmation])

  const handleTabChange = useCallback(
    (value: string) => {
      setSelectedTab(value as Tab)
      if (value === activityTab) {
        setNotification(undefined)
      }
    },
    [activityTab]
  )

  const switchToActivityTab = useCallback(() => {
    setSelectedTab(activityTab)
    setNotification(undefined)
  }, [activityTab])

  const onConfettiComplete = useCallback(() => {
    setShowConfetti(false)
  }, [])

  return {
    selectedTab,
    handleTabChange,
    notification,
    switchToActivityTab,
    showConfirmation,
    showConfetti,
    onConfettiComplete,
  }
}
